/**
 * API Client Instance
 * Centralized API client configuration for the Dorat application
 */

import { Api } from './Api';

// Base URL configuration
const baseURL = import.meta.env.VITE_API_BASE_URL || '/';

/**
 * Normalizes a failed response whose body is NOT JSON into `application/problem+json`.
 *
 * Why: several controllers hand the operator-facing reason back as `return BadRequest(ex.Message)`
 * (e.g. AdminCustomerReservationsController's cancel / process-return / complete-inspection /
 * process-deposit-refund). ASP.NET's StringOutputFormatter serializes a bare `string` as
 * `text/plain`, so the body is the sentence itself rather than ProblemDetails JSON. The generated
 * client is always called with `format: 'json'` and therefore runs `response.json()` on failures
 * too (`http-client.ts`), which throws — the thrown `error` became a `SyntaxError` and the UI
 * showed "Unexpected token …" instead of *why* the action failed (for instance why a deposit
 * release could not be routed to Stripe, and that the deferral escape hatch exists).
 *
 * Rewriting the body here keeps `extractProblemDetails` / `getErrorTitle` synchronous and fixes
 * every endpoint at once. Successful responses, and error bodies that are already JSON, are passed
 * through untouched.
 */
async function normalizeNonJsonErrorBody(response: Response): Promise<Response> {
	if (response.ok) return response;

	const contentType = response.headers.get('content-type') ?? '';

	if (contentType.includes('json')) return response;

	let body: string;

	try {
		body = await response.clone().text();
	} catch {
		// Body already consumed or unreadable — leave the response exactly as it was.
		return response;
	}

	const message = body.trim();

	if (message === '') return response;

	try {
		// A JSON body served without a JSON content-type: let the generated client parse it.
		if (typeof JSON.parse(message) === 'object') return response;
	} catch {
		// Not JSON — this is the plain-text case we are here to fix.
	}

	// `title` and `detail` both carry the sentence so consumers reading either one see it.
	return new Response(JSON.stringify({ status: response.status, title: message, detail: message }), {
		status: response.status,
		statusText: response.statusText,
		headers: { 'Content-Type': 'application/problem+json' }
	});
}

// Create API client instance with authentication
export const apiClient = new Api({
	baseUrl: baseURL,
	securityWorker: (securityData: string | null) => {
		return securityData ? { headers: { Authorization: `Bearer ${securityData}` } } : {};
	},
	customFetch: async (...args: Parameters<typeof fetch>) => normalizeNonJsonErrorBody(await fetch(...args))
});

// Export for convenience
export default apiClient;

// Re-export auth helpers
export { updateApiClientAuth, getApiClientAuthStatus } from './auth';
